import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, FormControl, ValidationErrors, Validator } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { CartItemComponentStore } from 'cart-data';
import { provideValueAccessor } from 'forms-ui';
import { ItemComponentBase } from 'products-ui';
import { CustomLength } from '../types';
import { WireCutCustomLengthViewComponent } from './views';
import { ItemPriceUtil } from 'products-data';

@Component({
  selector: 'ideal-wire-cut-custom-length',
  imports: [WireCutCustomLengthViewComponent, ReactiveFormsModule],
  templateUrl: './wire-cut-custom-length.component.html',
  styleUrl: './wire-cut-custom-length.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CartItemComponentStore, provideValueAccessor(WireCutCustomLengthComponent)],
})
export class WireCutCustomLengthComponent extends ItemComponentBase implements ControlValueAccessor, Validator {
  private readonly _store = inject(CartItemComponentStore);
  private readonly _viewComponent = viewChild.required(WireCutCustomLengthViewComponent);

  public readonly conflicted = input<boolean>(false);
  public readonly mode = input<'add' | 'edit'>('add');
  readonly lengthEnter = output<void>();
  readonly quantityEnter = output<void>();
  readonly deleteRequested = output<void>();

  private readonly _qty = signal<number>(0);
  private readonly _len = signal<number>(0);

  protected readonly pricing = computed(() =>
    ItemPriceUtil.calculatePrice(this._qty() * this._len(), this._store.cartItemPricing().pricingModel),
  );

  protected readonly internalControl = new FormControl<CustomLength | null>(null);

  private _onChange: (value: CustomLength | null) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor() {
    super();

    effect(() => {
      this._store.selectByItemId(this.itemId());
    });

    this.internalControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => {
      this._qty.set(v?.quantity ?? 0);
      this._len.set(v?.length ?? 0);
      this._onChange(v);
    });
  }

  writeValue(v: CustomLength | null): void {
    this._qty.set(v?.quantity ?? 0);
    this._len.set(v?.length ?? 0);
    this.internalControl.setValue(v ?? null, { emitEvent: false });
  }

  registerOnChange(fn: (value: CustomLength | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.internalControl.valid ? null : { invalid: true };
  }

  public reset(): void {
    this._viewComponent().reset();
  }

  public focusLength(): void {
    this._viewComponent().focusLength();
  }

  public focusQuantity(): void {
    this._viewComponent().focusQuantity();
  }
}
